import Accordion from "@/components/Accordion";
import Slider from "@/components/Slider";
import ColorPicker from "@/components/ColorPicker";
import { StyleConfigModel } from "@/pages/Messaging/types";
import { Toggle } from "@/components/Toggle";
import React from "react";
import { ChatWindow } from "../../types";
import LogoUpload from "../../LogoUpload";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
  setStagedLauncherIconFile?: (file: File | null) => void;
}

type LauncherPosition = "Left" | "Right";

const positions: LauncherPosition[] = ["Left", "Right"];

const shadowOptions = [
  { value: "none", label: "None" },
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" },
  { value: "xl", label: "Extra Large" }
];

const launcherSizes = [
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" }
];

export function Layout(props: Props) {
  const workingStyleConfig = props.workingChatWindow.styleConfig || {};

  const updateStyleConfig = (updates: Partial<StyleConfigModel>) => {
    props.setWorkingChatWindow({
      ...props.workingChatWindow,
      styleConfig: {
        ...workingStyleConfig,
        ...updates
      }
    });
  };

  function clampedNumber(
    e: React.ChangeEvent<HTMLInputElement>,
    min: number,
    max: number,
    fallback: number
  ) {
    let value = parseInt(e.target.value);
    if (isNaN(value)) value = fallback;
    if (value > max) value = max;
    if (value < min) value = min;
    return value;
  }

  return (
    <Accordion.Item eventKey="layout" onExpanded={() => props.onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Layout</Accordion.Title>
        <Accordion.SubTitle>
          Position, size, border, and shadow of your chat window
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="space-y-5">
          {/* Window Position */}
          <div>
            <div className="text-sm font-medium mb-2">Window Position</div>
            <div className="flex gap-4">
              {positions.map((position) => {
                const isSelected =
                  workingStyleConfig.launcherPosition === position;
                return (
                  <div
                    key={position}
                    className={`flex justify-center items-center rounded-lg py-2 px-6 w-32 cursor-pointer border-2 ${
                      isSelected
                        ? "border-cornflower-blue-500 bg-cornflower-blue-50"
                        : "border-slate-300 bg-white"
                    }`}
                    onClick={() =>
                      updateStyleConfig({ launcherPosition: position })
                    }
                  >
                    <div className="w-4 h-4 relative bg-slate-500 rounded-sm overflow-hidden">
                      <div
                        className={`w-[4px] h-[6px] top-[6px] absolute bg-white ${
                          position === "Left" ? "left-[2px]" : "right-[2px]"
                        }`}
                      />
                    </div>
                    <div className="text-sm font-medium text-slate-600 ml-2">
                      {position}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Horizontal Offset */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Horizontal Offset</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.sideSpacing ?? 20}
                  onChange={(e) =>
                    updateStyleConfig({
                      sideSpacing: clampedNumber(e, 0, 200, 0)
                    })
                  }
                  min={0}
                  max={200}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={200}
              min={0}
              onChange={(value) => updateStyleConfig({ sideSpacing: value })}
              value={workingStyleConfig.sideSpacing ?? 20}
            />
          </div>

          {/* Vertical Offset */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Vertical Offset</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.bottomSpacing ?? 20}
                  onChange={(e) =>
                    updateStyleConfig({
                      bottomSpacing: clampedNumber(e, 0, 200, 0)
                    })
                  }
                  min={0}
                  max={200}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={200}
              min={0}
              onChange={(value) => updateStyleConfig({ bottomSpacing: value })}
              value={workingStyleConfig.bottomSpacing ?? 20}
            />
          </div>

          <div className="border-t border-slate-200" />

          {/* Window Width */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Window Width</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowWidth ?? 370}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowWidth: clampedNumber(e, 320, 500, 370)
                    })
                  }
                  min={320}
                  max={500}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={500}
              min={320}
              onChange={(value) => updateStyleConfig({ windowWidth: value })}
              value={workingStyleConfig.windowWidth ?? 370}
            />
          </div>

          {/* Window Height */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Window Height</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowHeight ?? 0}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowHeight: clampedNumber(e, 0, 800, 0)
                    })
                  }
                  min={0}
                  max={800}
                  placeholder="Auto"
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={800}
              min={0}
              onChange={(value) => updateStyleConfig({ windowHeight: value })}
              value={workingStyleConfig.windowHeight ?? 0}
            />
            <div className="text-xs text-slate-400 mt-1">Set to 0 for auto height</div>
          </div>

          {/* Border Radius */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Corner Radius</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowBorderRadius ?? 32}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowBorderRadius: clampedNumber(e, 0, 32, 32)
                    })
                  }
                  min={0}
                  max={32}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={32}
              min={0}
              onChange={(value) =>
                updateStyleConfig({ windowBorderRadius: value })
              }
              value={workingStyleConfig.windowBorderRadius ?? 32}
            />
          </div>

          <div className="border-t border-slate-200" />

          {/* Border */}
          <div>
            <div className="text-sm font-medium mb-2">Border</div>
            <div className="flex items-center gap-4 mb-3">
              <div className="text-sm font-normal w-20">Width</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowBorderWidth ?? 0}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowBorderWidth: clampedNumber(e, 0, 5, 0)
                    })
                  }
                  min={0}
                  max={5}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            {(workingStyleConfig.windowBorderWidth ?? 0) > 0 && (
              <div className="flex items-center gap-4">
                <div className="text-sm font-normal w-20">Color</div>
                <ColorPicker
                  value={workingStyleConfig.windowBorderColor ?? "#e2e8f0"}
                  onChange={(value) =>
                    updateStyleConfig({ windowBorderColor: value })
                  }
                />
              </div>
            )}
          </div>

          {/* Shadow */}
          <div>
            <div className="text-sm font-medium mb-2">Shadow</div>
            <div className="flex flex-wrap gap-2">
              {shadowOptions.map((opt) => {
                const isSelected =
                  (workingStyleConfig.windowShadow ?? "xl") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-cornflower-blue-500 bg-cornflower-blue-50 text-cornflower-blue-700"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() =>
                      updateStyleConfig({ windowShadow: opt.value })
                    }
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Launcher Size */}
          <div>
            <div className="text-sm font-medium mb-2">Launcher Size</div>
            <div className="flex gap-3">
              {launcherSizes.map((size) => {
                const isSelected =
                  (workingStyleConfig.launcherSize ?? "md") === size.value;
                return (
                  <div
                    key={size.value}
                    className={`flex items-center justify-center rounded-lg py-2 px-5 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-cornflower-blue-500 bg-cornflower-blue-50 text-cornflower-blue-700"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() =>
                      updateStyleConfig({ launcherSize: size.value })
                    }
                  >
                    {size.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Launcher Appearance */}
          <div className="space-y-3">
            <div className="text-sm font-medium">Launcher Appearance</div>
            <div className="grid grid-cols-3 items-center gap-4">
              <div className="text-sm font-normal">Background Color</div>
              <ColorPicker
                value={workingStyleConfig.launcherBgColor ?? "#1670E7"}
                onChange={(value) => updateStyleConfig({ launcherBgColor: value })}
              />
              <div />
            </div>
            <div className="grid grid-cols-3 items-center gap-4">
              <div className="text-sm font-normal">Icon Color</div>
              <ColorPicker
                value={workingStyleConfig.launcherTextColor ?? "#FFFFFF"}
                onChange={(value) => updateStyleConfig({ launcherTextColor: value })}
              />
              <div />
            </div>
            <div>
              <div className="text-sm font-normal mb-1">Launcher Label</div>
              <input
                type="text"
                aria-label="Launcher Label"
                className="border border-slate-300 rounded-md p-2 w-full text-sm"
                placeholder="Optional text next to launcher icon"
                value={workingStyleConfig.launcherText ?? ""}
                onChange={(e) => updateStyleConfig({ launcherText: e.target.value })}
              />
            </div>
            {props.setStagedLauncherIconFile && (
              <LogoUpload
                setStageLogoFile={props.setStagedLauncherIconFile}
                update={updateStyleConfig}
                workingStyleConfig={workingStyleConfig}
                field="launcherIconUrl"
                label="Launcher Icon"
              />
            )}
          </div>

          <div className="border-t border-slate-200" />

          {/* Behavior toggles */}
          <div>
            <div className="text-sm font-medium mb-3">Behavior</div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Show unread message count</div>
                  <div className="text-xs text-slate-400">Badge on launcher when messages are unread</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.showUnreadCount ?? true}
                  changeToggle={() =>
                    updateStyleConfig({ showUnreadCount: !(workingStyleConfig.showUnreadCount ?? true) })
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Start minimized on desktop</div>
                  <div className="text-xs text-slate-400">Chat opens collapsed by default on desktop</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.forceMinimizedDesktop ?? false}
                  changeToggle={() =>
                    updateStyleConfig({ forceMinimizedDesktop: !workingStyleConfig.forceMinimizedDesktop })
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Start minimized on mobile</div>
                  <div className="text-xs text-slate-400">Chat opens collapsed by default on mobile</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.forceMinimizedMobile ?? false}
                  changeToggle={() =>
                    updateStyleConfig({ forceMinimizedMobile: !workingStyleConfig.forceMinimizedMobile })
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Fullscreen on mobile</div>
                  <div className="text-xs text-slate-400">Expands to fill the entire screen on mobile devices</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.fullscreenMobile ?? false}
                  changeToggle={() =>
                    updateStyleConfig({ fullscreenMobile: !workingStyleConfig.fullscreenMobile })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
